import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { IoMdAdd, IoMdTrash } from "react-icons/io";
import { MdEdit, MdClose } from "react-icons/md";
import { FaCrown } from "react-icons/fa";

const GangLevelConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formTiers, setFormTiers] = useState([]);
  const [formIsActive, setFormIsActive] = useState(true);

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/gang-level-config/get-all`
      );
      setConfigs(res.data?.configs || []);
    } catch (error) {
      console.error("Error fetching level configs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const resetForm = () => {
    setFormTiers([]);
    setFormIsActive(true);
    setEditingConfig(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (config) => {
    setEditingConfig(config);
    setFormTiers(
      (config.tiers || []).map((t) => ({
        level: t.level,
        threshold: t.threshold,
        title: t.title,
        activeHours: {
          hoursPerDay: t.activeHours?.hoursPerDay ?? "",
          rankPoints: t.activeHours?.rankPoints ?? "",
        },
        games: {
          gamesPerDay: t.games?.gamesPerDay ?? "",
          rankPoints: t.games?.rankPoints ?? "",
        },
        giftCoins: {
          giftCoinsPerDay: t.giftCoins?.giftCoinsPerDay ?? "",
          rankPoints: t.giftCoins?.rankPoints ?? "",
        },
      }))
    );
    setFormIsActive(config.isActive);
    setShowModal(true);
  };

  const createEmptyTier = (nextLevel) => ({
    level: nextLevel,
    threshold: 0,
    title: "",
    activeHours: { hoursPerDay: "", rankPoints: "" },
    games: { gamesPerDay: "", rankPoints: "" },
    giftCoins: { giftCoinsPerDay: "", rankPoints: "" },
  });

  const addTier = () => {
    const nextLevel =
      formTiers.length > 0
        ? Math.max(...formTiers.map((t) => t.level)) + 5
        : 1;
    setFormTiers([...formTiers, createEmptyTier(nextLevel)]);
  };

  const updateTier = (index, field, value) => {
    const updated = [...formTiers];
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      updated[index] = {
        ...updated[index],
        [parent]: {
          ...updated[index][parent],
          [child]: value,
        },
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setFormTiers(updated);
  };

  const removeTier = (index) => {
    setFormTiers(formTiers.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formTiers.length === 0) {
      alert("Add at least one level tier");
      return;
    }
    for (const tier of formTiers) {
      if (!tier.title || tier.level <= 0) {
        alert("Each tier must have a level > 0 and a title");
        return;
      }
    }

    // Build tiers with numeric values for target configs
    const sortedTiers = [...formTiers]
      .sort((a, b) => a.threshold - b.threshold)
      .map((t) => ({
        level: Number(t.level),
        threshold: Number(t.threshold),
        title: t.title,
        activeHours: {
          hoursPerDay: t.activeHours.hoursPerDay !== "" ? Number(t.activeHours.hoursPerDay) : null,
          rankPoints: t.activeHours.rankPoints !== "" ? Number(t.activeHours.rankPoints) : null,
        },
        games: {
          gamesPerDay: t.games.gamesPerDay !== "" ? Number(t.games.gamesPerDay) : null,
          rankPoints: t.games.rankPoints !== "" ? Number(t.games.rankPoints) : null,
        },
        giftCoins: {
          giftCoinsPerDay: t.giftCoins.giftCoinsPerDay !== "" ? Number(t.giftCoins.giftCoinsPerDay) : null,
          rankPoints: t.giftCoins.rankPoints !== "" ? Number(t.giftCoins.rankPoints) : null,
        },
      }));

    const payload = { tiers: sortedTiers, isActive: formIsActive };

    try {
      if (editingConfig) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/gang-level-config/update/${editingConfig._id}`,
          payload
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/gang-level-config/create`,
          payload
        );
      }
      setShowModal(false);
      resetForm();
      fetchConfigs();
    } catch (error) {
      console.error("Error saving config:", error);
      alert("Failed to save config");
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm("Are you sure you want to delete this config?")) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/gang-level-config/delete/${configId}`
      );
      fetchConfigs();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete");
    }
  };

  return (
    <div className="mx-auto mt-5 max-w-6xl px-4">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Gang Level Configuration
          </h1>
          <p className="text-sm text-gray-500">
            Configure level tiers and per-level targets for gangs
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
        >
          <IoMdAdd size={18} /> New Config
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="py-20 text-center text-gray-400">Loading...</div>
      )}

      {/* Empty */}
      {!loading && configs.length === 0 && (
        <div className="rounded-xl bg-white p-12 text-center shadow">
          <FaCrown className="mx-auto mb-3 text-5xl text-purple-300" />
          <p className="text-lg font-medium text-gray-500">
            No level configs yet
          </p>
          <p className="text-sm text-gray-400">
            Click "New Config" to define gang level tiers
          </p>
        </div>
      )}

      {/* Config Cards */}
      {configs.map((config) => (
        <div
          key={config._id}
          className={`mb-4 rounded-xl bg-white p-5 shadow ${
            config.isActive
              ? "border-2 border-purple-500"
              : "border border-gray-200"
          }`}
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FaCrown
                className={
                  config.isActive ? "text-purple-600" : "text-gray-400"
                }
                size={20}
              />
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  config.isActive
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {config.isActive ? "ACTIVE" : "INACTIVE"}
              </span>
              <span className="text-sm text-gray-400">
                {config.tiers?.length || 0} tiers
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => openEditModal(config)}
                className="rounded-lg bg-blue-50 p-2 text-blue-600 hover:bg-blue-100"
              >
                <MdEdit size={18} />
              </button>
              {!config.isActive && (
                <button
                  onClick={() => handleDelete(config._id)}
                  className="rounded-lg bg-red-50 p-2 text-red-500 hover:bg-red-100"
                >
                  <IoMdTrash size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Tiers Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b text-gray-500">
                  <th className="py-2 pr-4">Level</th>
                  <th className="py-2 pr-4">Rank Pts Threshold</th>
                  <th className="py-2 pr-4">Title</th>
                  <th className="py-2 pr-4">Active Hours</th>
                  <th className="py-2 pr-4">Games</th>
                  <th className="py-2">Gift Coins</th>
                </tr>
              </thead>
              <tbody>
                {(config.tiers || []).map((tier, idx) => (
                  <tr key={idx} className="border-b border-gray-50">
                    <td className="py-2 pr-4 font-semibold text-purple-700">
                      Lv. {tier.level}
                    </td>
                    <td className="py-2 pr-4 text-gray-600">
                      {tier.threshold.toLocaleString()}
                    </td>
                    <td className="py-2 pr-4 text-gray-700">{tier.title}</td>
                    <td className="py-2 pr-4 text-gray-600">
                      {tier.activeHours?.hoursPerDay != null ? (
                        <span>
                          {tier.activeHours.hoursPerDay}h → {tier.activeHours.rankPoints} pts
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-gray-600">
                      {tier.games?.gamesPerDay != null ? (
                        <span>
                          {tier.games.gamesPerDay}g → {tier.games.rankPoints} pts
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-2 text-gray-600">
                      {tier.giftCoins?.giftCoinsPerDay != null ? (
                        <span>
                          {tier.giftCoins.giftCoinsPerDay}c → {tier.giftCoins.rankPoints} pts
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">
                {editingConfig ? "Edit Level Config" : "Create Level Config"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <MdClose size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Active toggle */}
              <label className="mb-4 flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formIsActive}
                  onChange={(e) => setFormIsActive(e.target.checked)}
                  className="h-4 w-4 accent-purple-600"
                />
                <span className="text-sm font-medium text-gray-700">
                  Set as Active Config
                </span>
              </label>

              {/* Tiers */}
              <div className="mb-4">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700">Level Tiers</h3>
                  <button
                    type="button"
                    onClick={addTier}
                    className="flex items-center gap-1 rounded-lg bg-purple-50 px-3 py-1 text-sm text-purple-600 hover:bg-purple-100"
                  >
                    <IoMdAdd size={16} /> Add Tier
                  </button>
                </div>

                {formTiers.length === 0 && (
                  <p className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-400">
                    No tiers. Click "Add Tier" to start.
                  </p>
                )}

                {formTiers.map((tier, idx) => (
                  <div
                    key={idx}
                    className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-4"
                  >
                    {/* Row 1: Level, Threshold, Title */}
                    <div className="mb-3 flex items-center gap-3">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">Level</label>
                        <input
                          type="number"
                          min="1"
                          value={tier.level}
                          onChange={(e) =>
                            updateTier(idx, "level", parseInt(e.target.value) || 1)
                          }
                          className="w-full rounded border px-2 py-1 text-sm"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">
                          Rank Points Threshold
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={tier.threshold}
                          onChange={(e) =>
                            updateTier(
                              idx,
                              "threshold",
                              parseInt(e.target.value) || 0
                            )
                          }
                          className="w-full rounded border px-2 py-1 text-sm"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">Title</label>
                        <input
                          type="text"
                          value={tier.title}
                          onChange={(e) =>
                            updateTier(idx, "title", e.target.value)
                          }
                          placeholder="e.g. Elite"
                          className="w-full rounded border px-2 py-1 text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTier(idx)}
                        className="mt-4 text-red-400 hover:text-red-600"
                      >
                        <IoMdTrash size={18} />
                      </button>
                    </div>

                    {/* Row 2: Target Configs */}
                    <div className="grid grid-cols-3 gap-3">
                      {/* Active Hours */}
                      <div className="rounded border border-blue-100 bg-blue-50/50 p-2">
                        <p className="mb-1 text-xs font-semibold text-blue-600">
                          Active Hours
                        </p>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-[10px] text-gray-400">Hrs/Day</label>
                            <input
                              type="number"
                              min="0"
                              value={tier.activeHours.hoursPerDay}
                              onChange={(e) =>
                                updateTier(idx, "activeHours.hoursPerDay", e.target.value === "" ? "" : Number(e.target.value))
                              }
                              placeholder="—"
                              className="w-full rounded border px-1.5 py-0.5 text-xs"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-[10px] text-gray-400">Rank Pts</label>
                            <input
                              type="number"
                              min="0"
                              value={tier.activeHours.rankPoints}
                              onChange={(e) =>
                                updateTier(idx, "activeHours.rankPoints", e.target.value === "" ? "" : Number(e.target.value))
                              }
                              placeholder="—"
                              className="w-full rounded border px-1.5 py-0.5 text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Games */}
                      <div className="rounded border border-green-100 bg-green-50/50 p-2">
                        <p className="mb-1 text-xs font-semibold text-green-600">
                          Games
                        </p>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-[10px] text-gray-400">Games/Day</label>
                            <input
                              type="number"
                              min="0"
                              value={tier.games.gamesPerDay}
                              onChange={(e) =>
                                updateTier(idx, "games.gamesPerDay", e.target.value === "" ? "" : Number(e.target.value))
                              }
                              placeholder="—"
                              className="w-full rounded border px-1.5 py-0.5 text-xs"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-[10px] text-gray-400">Rank Pts</label>
                            <input
                              type="number"
                              min="0"
                              value={tier.games.rankPoints}
                              onChange={(e) =>
                                updateTier(idx, "games.rankPoints", e.target.value === "" ? "" : Number(e.target.value))
                              }
                              placeholder="—"
                              className="w-full rounded border px-1.5 py-0.5 text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Gift Coins */}
                      <div className="rounded border border-orange-100 bg-orange-50/50 p-2">
                        <p className="mb-1 text-xs font-semibold text-orange-600">
                          Gift Coins
                        </p>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-[10px] text-gray-400">Coins/Day</label>
                            <input
                              type="number"
                              min="0"
                              value={tier.giftCoins.giftCoinsPerDay}
                              onChange={(e) =>
                                updateTier(idx, "giftCoins.giftCoinsPerDay", e.target.value === "" ? "" : Number(e.target.value))
                              }
                              placeholder="—"
                              className="w-full rounded border px-1.5 py-0.5 text-xs"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-[10px] text-gray-400">Rank Pts</label>
                            <input
                              type="number"
                              min="0"
                              value={tier.giftCoins.rankPoints}
                              onChange={(e) =>
                                updateTier(idx, "giftCoins.rankPoints", e.target.value === "" ? "" : Number(e.target.value))
                              }
                              placeholder="—"
                              className="w-full rounded border px-1.5 py-0.5 text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="rounded-lg border px-4 py-2 text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-purple-600 px-6 py-2 text-white hover:bg-purple-700"
                >
                  {editingConfig ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GangLevelConfigPage;
